// Shell de l'UI + vue Diagnostic. Trois vues : Diagnostic / Panic iPhone /
// Éditeur KB, commutées par une barre d'onglets. Mobile-first.

import type { BoardModel, DiagnoseResult, Rail } from "../domain/types";
import { confirmOutcome, diagnose } from "../diagnostic/engine";
import { listBoards, getSyncState, putSyncState } from "../storage/db";
import { empreinteDuPoste } from "../core/identity";
import { getLicenseToken, setLicenseToken, verifyLicense } from "../core/license";
import { syncNow, type SyncReport } from "../core/sync";
import { $, el, esc, toast } from "./dom";
import { printFiche } from "./printSheet";
import { renderBoardEditor } from "./boardEditor";
import { renderPanicView, type PanicPrefill } from "./panicView";
import { announceResults, setVoiceContext, toggleVoice, voiceSupported } from "../voice/voice";
import { LOGO_DATA_URI } from "./logo";

let boards: BoardModel[] = [];
type Prefill = { symptom?: string; board_model_id?: string | null; device_brand?: string | null; device_model?: string | null };
let prefill: Prefill | null = null;
let lastRun: { symptom: string; appareil: string } | null = null;
let lastResult: DiagnoseResult | null = null;

export async function mountUI(root: HTMLElement): Promise<void> {
  boards = await listBoards();
  root.innerHTML = "";
  root.appendChild(
    el(`<header class="topbar">
      <img alt="" src="${LOGO_DATA_URI}" style="height:32px;width:32px;border-radius:50%;object-fit:cover;flex:0 0 auto">
      <span class="logo">THOR<b>·</b>Diagnostique</span>
      <span class="spacer"></span>
      <span class="sync-badge" id="sync"><span class="dot" id="dot"></span><span id="syncTxt">…</span></span>
    </header>`),
  );
  root.appendChild(
    el(`<nav class="tabs">
      <button class="tab on" data-view="diagnostic">Diagnostic</button>
      <button class="tab" data-view="panic">Panic iPhone</button>
      <button class="tab" data-view="kb">Éditeur KB</button>
    </nav>`),
  );
  root.appendChild(el(`<div id="view"></div>`));
  root.appendChild(el(`<div class="toast" id="toast"></div>`));

  root.querySelectorAll<HTMLElement>(".tab").forEach((b) =>
    b.addEventListener("click", () => void showView(b.dataset.view as string)),
  );
  await showView("diagnostic");
}

async function showView(name: string): Promise<void> {
  document.querySelectorAll<HTMLElement>(".tab").forEach((t) => t.classList.toggle("on", t.dataset.view === name));
  const host = $("#view");
  host.innerHTML = "";
  if (name === "panic") {
    renderPanicView(host, goToDiagnosticFromPanic);
  } else if (name === "kb") {
    await renderBoardEditor(host, async () => {
      boards = await listBoards();
    });
  } else {
    await renderDiagnosticView(host);
  }
}

function goToDiagnosticFromPanic(p: PanicPrefill): void {
  prefill = { symptom: p.symptom, device_brand: p.device_brand, device_model: p.device_model, board_model_id: null };
  void showView("diagnostic");
}

// ── Vue Diagnostic ─────────────────────────────────────────────────────────
async function renderDiagnosticView(host: HTMLElement): Promise<void> {
  host.appendChild(
    el(`<section class="card">
      <h2>Nouveau diagnostic</h2>
      <label>Carte / appareil</label>
      <select id="board"></select>
      <label>Symptôme</label>
      <div class="chips" id="sympChips"></div>
      <textarea id="symptom" placeholder="Décris le symptôme (ex. : pas de démarrage, PPBUS à 0 V…)"></textarea>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
        <button class="btn" id="run">🔍 Diagnostiquer</button>
        <button class="btn ghost" id="mic" style="display:none;" title="Mode vocal mains libres — « carte A2337 », « symptôme … », « diagnostic », « suivant »">🎤 Voix</button>
      </div>
      <div class="muted" id="faultcount"></div>
    </section>`),
  );
  host.appendChild(
    el(`<section class="card">
      <h2>Licence</h2>
      <div id="licStatus" class="muted">Vérification…</div>

      <label>Empreinte de ce poste</label>
      <div class="muted" style="font-size:13px;margin-bottom:6px;">
        À communiquer à THOR pour obtenir une licence liée à cet appareil.
      </div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:16px;">
        <code id="empreinte" style="font-size:15px;letter-spacing:.06em;padding:9px 12px;border-radius:8px;background:rgba(255,255,255,.06);user-select:all;">…</code>
        <button class="btn ghost" id="empreinteCopy" type="button">📋 Copier</button>
      </div>

      <label>Clé de licence (THOR-LIC-…)</label>
      <textarea id="licKey" placeholder="Colle ta clé de licence ici"></textarea>
      <button class="btn ghost" id="licSave" style="margin-top:12px;">Activer la licence</button>
      ${contactBlock("acheter ou renouveler une licence")}
    </section>`),
  );
  host.appendChild(el(`<div id="results"></div>`));

  const boardSel = $("#board") as HTMLSelectElement;
  boardSel.appendChild(el(`<option value="">— Générique / non listé —</option>`));
  for (const b of boards) boardSel.appendChild(el(`<option value="${esc(b.id)}">${b.locked ? "🔒 " : ""}${esc(b.brand)} ${esc(b.model)}</option>`));
  boardSel.addEventListener("change", renderSymptomChips);

  // Pré-remplissage (ex. depuis un panic décodé).
  if (prefill) {
    if (prefill.board_model_id) boardSel.value = prefill.board_model_id;
    if (prefill.symptom) ($("#symptom") as HTMLTextAreaElement).value = prefill.symptom;
  }
  renderSymptomChips();

  $("#run").addEventListener("click", run);
  $("#licSave").addEventListener("click", saveLicense);
  void afficherEmpreinte();

  // Mode vocal mains libres (si le navigateur a la reconnaissance vocale)
  if (voiceSupported()) {
    const mic = $("#mic") as HTMLButtonElement;
    mic.style.display = "";
    setVoiceContext({
      boards: () => boards,
      selectBoard: (id) => {
        boardSel.value = id;
        renderSymptomChips();
      },
      setSymptom: (text) => {
        ($("#symptom") as HTMLTextAreaElement).value = text;
      },
      runDiagnostic: run,
      lastResult: () => lastResult,
    });
    mic.addEventListener("click", () =>
      toggleVoice((listening) => {
        mic.classList.toggle("on", listening);
        mic.textContent = listening ? "🎤 Écoute…" : "🎤 Voix";
      }),
    );
  }

  await refreshLicenseStatus();
  refreshFaultCount(await import("../storage/db").then((m) => m.countFaults()));
}

function renderSymptomChips(): void {
  const wrap = $("#sympChips");
  wrap.innerHTML = "";
  const b = boards.find((x) => x.id === ($("#board") as HTMLSelectElement).value);
  if (!b) return;
  for (const s of b.symptoms) {
    const chip = el(`<span class="chip">${esc(s.label)}</span>`);
    chip.addEventListener("click", () => (($("#symptom") as HTMLTextAreaElement).value = s.label));
    wrap.appendChild(chip);
  }
}

async function run(): Promise<void> {
  const symptom = ($("#symptom") as HTMLTextAreaElement).value.trim();
  if (!symptom) return toast("Renseigne un symptôme.");
  const boardId = ($("#board") as HTMLSelectElement).value || null;
  const b = boards.find((x) => x.id === boardId);
  if (b?.locked) {
    renderLocked(b);
    $("#results").scrollIntoView({ behavior: "smooth" });
    return;
  }
  const device_brand = b?.brand ?? prefill?.device_brand ?? null;
  const device_model = b?.model ?? prefill?.device_model ?? null;

  const btn = $("#run") as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = "Analyse…";
  try {
    const res = await diagnose({ symptom, device_brand, device_model, board_model_id: boardId });
    lastRun = { symptom, appareil: b?.model ?? device_model ?? "—" };
    lastResult = res;
    renderResults(res);
    announceResults(); // lecture vocale si le mode mains libres est actif
    $("#results").scrollIntoView({ behavior: "smooth" });
  } finally {
    btn.disabled = false;
    btn.textContent = "🔍 Diagnostiquer";
    prefill = null; // consommé
  }
}

function renderResults(res: DiagnoseResult): void {
  const out = $("#results");
  out.innerHTML = "";
  const srcLabel = res.source === "core" ? "☁ core" : res.source === "local" ? "⛌ local" : "∅";

  const card = el(`
    <section class="card">
      <h2 style="display:flex;justify-content:space-between;align-items:center;">
        Causes probables <span class="src ${res.source}">${srcLabel}</span>
      </h2>
      <div id="causes"></div>
      <button class="btn ghost" id="print" style="margin-top:14px;">🖨 Imprimer la fiche</button>
    </section>`);
  out.appendChild(card);
  const cc = card.querySelector("#causes") as HTMLElement;

  if (res.causes.length === 0) {
    cc.appendChild(
      el(`<p class="empty-note">Aucune panne connue ne correspond. Le diagnostic est enregistré et enrichira la base après synchronisation.</p>`),
    );
  } else {
    for (const c of res.causes) {
      const pct = Math.round(c.weight * 100);
      const item = el(`
        <div class="cause">
          <div class="head"><strong>${esc(c.cause)}</strong><span class="pct">${pct}%</span></div>
          <div class="bar"><i style="width:${pct}%"></i></div>
          ${c.procedure ? `<div class="proc">${esc(c.procedure)}</div>` : ""}
          <div class="cause-actions">
            <button class="btn fb ok">✓ C'était ça — réparé</button>
            <button class="btn fb">Cause retenue</button>
          </div>
        </div>`);
      const [okBtn, keepBtn] = item.querySelectorAll(".btn.fb");
      okBtn.addEventListener("click", () => sendFeedback(res.event_id, c.cause, true));
      keepBtn.addEventListener("click", () => sendFeedback(res.event_id, c.cause, false));
      cc.appendChild(item);
    }
  }
  card.querySelector("#print")!.addEventListener("click", () =>
    printFiche(res, { appareil: lastRun?.appareil, symptom: lastRun?.symptom ?? "" }),
  );

  if (res.board) renderRails(out, res.board);

  if (res.symptom_card && res.board) {
    const mp = new Map(res.board.measure_points.map((m) => [m.label, m]));
    const c2 = el(`<section class="card"><h2>Procédure — ${esc(res.symptom_card.label)}</h2><div id="steps"></div></section>`);
    const steps = c2.querySelector("#steps") as HTMLElement;
    res.symptom_card.checklist.forEach((step, i) => {
      const m = mp.get(step);
      steps.appendChild(
        el(`<div class="step"><span class="n">${i + 1}</span><span>${esc(step)}${
          m ? ` <span class="mp">→ <span class="exp">${esc(m.expected)}</span></span>` : ""
        }</span></div>`),
      );
    });
    out.appendChild(c2);
  }
}

// Contact THOR pour l'achat / le renouvellement de licence. Lien WhatsApp
// pré-rempli (s'ouvre directement sur une conversation) + email.
const THOR_WHATSAPP = "2250778484847";          // +225 07 78 48 48 47
const THOR_EMAIL = "contact@thor-technologie.com";

function contactBlock(reason: string): string {
  const wa = `https://wa.me/${THOR_WHATSAPP}?text=${encodeURIComponent(
    `Bonjour THOR, je souhaite ${reason} pour THOR Diagnostique.`,
  )}`;
  return `
    <div class="contact-thor" style="margin-top:14px;border-top:1px solid var(--border,#2a2f3a);padding-top:12px;">
      <p style="margin:0 0 8px;font-weight:600;">Pour obtenir votre clé :</p>
      <a class="btn" href="${wa}" target="_blank" rel="noopener" style="display:inline-block;margin:0 8px 8px 0;">💬 WhatsApp THOR</a>
      <a class="btn ghost" href="mailto:${THOR_EMAIL}?subject=${encodeURIComponent("Licence THOR Diagnostique")}" style="display:inline-block;">✉️ ${THOR_EMAIL}</a>
    </div>`;
}

function renderLocked(board: BoardModel): void {
  const out = $("#results");
  out.innerHTML = "";
  out.appendChild(el(
    `<section class="card">
      <h2>🔒 ${esc(board.brand)} ${esc(board.model)}</h2>
      <p>Cette carte fait partie de la <strong>base complète</strong> : rails, tensions, points de mesure et pannes connues.</p>
      <p>Débloque <strong>toute la base de connaissances</strong> + les mises à jour en continu :</p>
      <p style="font-size:1.4em;color:var(--amber)"><strong>45 000 CFA / an</strong></p>
      <p>Ouvre l'onglet <strong>Licence</strong>, colle ta clé, et ce poste est activé immédiatement.</p>
      ${contactBlock("acheter une licence")}
    </section>`
  ));
}

function renderRails(out: HTMLElement, board: BoardModel): void {
  const order: Rail["state"][] = ["G3", "always_on", "S5", "S4", "S3", "S0"];
  const sorted = [...board.rails].sort((a, b) => order.indexOf(a.state) - order.indexOf(b.state));
  // fiche générée depuis un schéma (carte sœur) : le dire au technicien avant qu'il s'y fie
  const avert = board.provisoire
    ? `<p class="muted" style="border-left:3px solid var(--amber);padding-left:8px">⚠ <strong>Fiche provisoire</strong> — ${esc(board.note_provisoire || "à valider à l'atelier")}${board.soeur ? ` Carte sœur : ${esc(board.soeur)}.` : ""}</p>`
    : "";
  const card = el(`<section class="card"><h2>Rails — ${esc(board.model)}</h2>${avert}<div id="rails"></div></section>`);
  const rl = card.querySelector("#rails") as HTMLElement;
  for (const r of sorted) {
    rl.appendChild(
      el(`<div class="rail">
        <div><span class="name">${esc(r.name)}</span> · <span class="v">${r.nominal_v} V</span> <span class="state-tag">${esc(r.state)}</span></div>
        ${r.source_component ? `<div class="meta">${esc(r.source_component)}${r.parent ? ` ← ${esc(r.parent)}` : ""}</div>` : ""}
      </div>`),
    );
  }
  out.appendChild(card);
}

async function sendFeedback(eventId: string, cause: string, resolved: boolean): Promise<void> {
  await confirmOutcome(eventId, cause, resolved);
  toast(resolved ? "Merci — confirmé. Remontera au core à la prochaine sync." : "Cause retenue, à confirmer après réparation.");
}

// ── Empreinte du poste ───────────────────────────────────────────────────-─
// Affichée pour que le client puisse la transmettre : le plan diagnostic_1an
// exige une licence liée à la machine (fingerprint_required), or l'empreinte
// n'était jusqu'ici lisible que côté serveur.
async function afficherEmpreinte(): Promise<void> {
  const node = document.getElementById("empreinte");
  const bouton = document.getElementById("empreinteCopy");
  if (!node) return;
  let valeur: string;
  try {
    valeur = await empreinteDuPoste();
  } catch {
    node.textContent = "indisponible";
    return;
  }
  node.textContent = valeur;
  bouton?.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(valeur);
      toast("Empreinte copiée.");
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : le <code>
      // est en user-select:all, un appui long suffit à sélectionner.
      toast("Copie impossible — sélectionne l'empreinte à la main.");
    }
  });
}

// ── Licence ──────────────────────────────────────────────────────────────-─
async function refreshLicenseStatus(): Promise<void> {
  const node = document.getElementById("licStatus");
  if (!node) return;
  const token = await getLicenseToken();
  if (!token) {
    node.innerHTML = `<span style="color:var(--amber)">Mode démo — fonctionne et synchronise. Saisis ta clé pour activer ta licence.</span>`;
    return;
  }
  const lic = await verifyLicense(token);
  if (lic) {
    node.innerHTML = `<span style="color:var(--green)">✓ Licence valide</span> — ${esc(lic.licensee_name)}`;
    (document.getElementById("licKey") as HTMLTextAreaElement).value = token;
  } else {
    node.innerHTML = `<span style="color:var(--red)">✗ Clé invalide ou illisible.</span>`;
  }
}

async function saveLicense(): Promise<void> {
  const ta = document.getElementById("licKey") as HTMLTextAreaElement;
  const token = ta.value.trim();
  if (!token) return toast("Colle ta clé de licence.");
  const lic = await verifyLicense(token);
  if (!lic) return toast("Clé invalide — vérifie le copier-coller.");
  await setLicenseToken(token);
  await refreshLicenseStatus();
  toast(`Licence activée : ${lic.licensee_name}. Déverrouillage de la base…`);
  // Réinitialiser le curseur boards → re-télécharger la base COMPLÈTE : le poste
  // vient d'être promu licencié, le core renverra les payloads complets (plus de stubs).
  const st = await getSyncState();
  await putSyncState({ ...st, boards_cursor: null });
  await syncNow();      // ré-enrôle (promotion côté core) + pull complet de la KB
  location.reload();    // recharge l'UI avec la base déverrouillée
}

// ── Exports pour main.ts ───────────────────────────────────────────────────
/** Recharge la liste des cartes (après une sync) et repeuple le sélecteur
 *  de la vue Diagnostic s'il est affiché — sans toucher à la sélection. */
export async function refreshBoards(): Promise<void> {
  boards = await listBoards();
  const sel = document.getElementById("board") as HTMLSelectElement | null;
  if (!sel) return; // autre vue affichée : la liste sera fraîche au prochain rendu
  const cur = sel.value;
  sel.innerHTML = "";
  sel.appendChild(el(`<option value="">— Générique / non listé —</option>`));
  for (const b of boards) {
    sel.appendChild(el(`<option value="${esc(b.id)}">${b.locked ? "🔒 " : ""}${esc(b.brand)} ${esc(b.model)}</option>`));
  }
  if (cur) sel.value = cur;
  renderSymptomChips();
}

export function updateSyncBadge(report: SyncReport | "init"): void {
  const dot = document.getElementById("dot");
  const txt = document.getElementById("syncTxt");
  if (!dot || !txt) return;
  const online = navigator.onLine;
  dot.className = "dot " + (online ? "online" : "offline");
  if (report === "init") txt.textContent = online ? "en ligne" : "hors-ligne";
  else if (report.ok) txt.textContent = `sync ✓ ↑${report.pushed} ↓${report.pulled}`;
  else txt.textContent = online ? "local" : "hors-ligne";
}

export function refreshFaultCount(n: number): void {
  const c = document.getElementById("faultcount");
  if (c) c.textContent = `${n} panne(s) en base locale`;
}
